/** Review slice — the reference `review()` method (design-reference script lines 3106–3541): the journal/eod shared day,
 *  the Trade Replay (seeded series + TradingView bars), the backtest (`runBacktest`, script 909–949) and the account-progress
 *  summary. Ported verbatim; data comes from `c.data.core` (REV_DAYS/REV_ACCTS) and `c.data.review` (BT_BOOKS, replay meta).
 *  Keys the template never binds (the generated ViewModel has no member for them) are omitted — see the slice report. */
import type { ChangeEvent } from 'react';
import type { BtBook, RevDay, RevEntry } from '@pointfive/contracts';
import { tvMarkers } from './charts';
import type { Ctx } from './ctx';
import type { BacktestRun, ModelState } from './state';
import type { ViewModel } from '@/screens/generated/view-model';

/** One replay trade — a REV_DAYS entry keyed to its day (`d` is the day's short label). */
export interface ReplayTrade {
  d: string;
  sym: string;
  side: string;
  r: number;
  pnl: number;
  strat: string;
  time: string;
  grade: string;
  note: string;
}
/** Narrow an indexed read the reference logic guarantees (fixed-length seeded series, non-empty datasets). */
function must<T>(v: T | undefined, what: string): T {
  if (v === undefined) throw new Error('review: ' + what + ' is missing');
  return v;
}

/** The backtest form the solver reads. */
export type BacktestForm = Pick<ModelState, 'btStrat' | 'btWindow' | 'btRisk' | 'btMonths' | 'btCap'>;

/** Solves the backtest — seeded from the form against the strategy's book (the pure core of the reference runBacktest()). */
export function solveBacktest(S: BacktestForm, books: Record<string, BtBook>): BacktestRun {
  const B = must(books[S.btStrat], 'BT_BOOKS[' + S.btStrat + ']');
  let seed = 0;
  (S.btStrat + S.btWindow + S.btRisk + S.btMonths + S.btCap).split('').forEach((ch) => {
    seed = (seed * 31 + ch.charCodeAt(0)) % 233280;
  });
  const rnd = () => (seed = (seed * 9301 + 49297) % 233280) / 233280;
  const days = Math.round(S.btMonths * 21);
  const trades: { d: number; r: number; win: boolean }[] = [];
  let eq = 0,
    peak = 0,
    maxDD = 0,
    streak = 0,
    worstStreak = 0;
  const curve: { i: number; eq: number }[] = [{ i: 0, eq: 0 }];
  for (let d = 0; d < days; d++) {
    const n = Math.min(S.btCap, Math.round(B.perDay + (rnd() - 0.5)));
    for (let k = 0; k < n; k++) {
      const win = rnd() < B.wr;
      const r = win ? B.avgWin * (0.65 + rnd() * 0.75) : B.avgLoss * (0.7 + rnd() * 0.5);
      trades.push({ d, r, win });
      eq += r;
      if (win) streak = 0;
      else {
        streak++;
        worstStreak = Math.max(worstStreak, streak);
      }
      peak = Math.max(peak, eq);
      maxDD = Math.max(maxDD, peak - eq);
    }
    curve.push({ i: d + 1, eq });
  }
  const wins = trades.filter((t) => t.win),
    losses = trades.filter((t) => !t.win);
  const grossWin = wins.reduce((a, t) => a + t.r, 0),
    grossLoss = losses.reduce((a, t) => a + t.r, 0);
  return {
    strat: S.btStrat,
    window: S.btWindow,
    risk: S.btRisk,
    months: S.btMonths,
    cap: S.btCap,
    days,
    n: trades.length,
    wins: wins.length,
    losses: losses.length,
    netR: eq,
    expectancy: trades.length ? eq / trades.length : 0,
    wr: trades.length ? wins.length / trades.length : 0,
    pf: grossLoss ? Math.abs(grossWin / grossLoss) : 0,
    avgWin: wins.length ? grossWin / wins.length : 0,
    avgLoss: losses.length ? grossLoss / losses.length : 0,
    maxDD,
    worstStreak,
    curve,
  };
}

/** The backtest the "Run backtest" button schedules (reference runBacktest()) — reads the live form, writes `btRun`, clears `btBusy`. */
export function runBacktest(c: Ctx): void {
  c.set((s) => ({ btBusy: false, btRun: solveBacktest(s, c.data.review.btBooks) }));
}

/** A day with nothing in it — the state of every account before its first journalled day.
 *
 *  Not a fabricated day: no trades, no mood, no note, and a label that says so. The Review screen needs a shape to
 *  render its empty state THROUGH; refusing to supply one meant the screen could not draw the absence it was
 *  reporting, and because the dataset is composed once for the whole app, that took every other screen with it. */
const NO_DAY: RevDay = {
  label: 'No journalled days yet',
  short: '—',
  mood: '',
  moodIcon: '',
  energy: 0,
  entries: [],
  ai: ['', ''],
};

function dayAt(days: RevDay[], i: number): RevDay {
  if (days.length === 0) return NO_DAY;
  return must(days[Math.min(i, days.length - 1)], 'REV_DAYS[' + i + ']');
}
const toReplayTrade =
  (RD: RevDay) =>
  (e: RevEntry): ReplayTrade => ({
    d: RD.short,
    sym: e[0],
    side: e[1],
    r: e[2],
    pnl: e[3],
    strat: e[4],
    time: e[5],
    grade: e[6],
    note: e[7],
  });
/* `replayBars()` and `replayTarget()` used to live here: a client-side PRNG that synthesised sixty candles from a
 * hash of the trade's symbol, day and time, handed to a SECOND TradingView datafeed in `screens/replayChart.ts`.
 * Both are gone. Nothing mounted that datafeed — the Replay screen renders `<pf-tv-chart mode="replay">` like every
 * other pane — so it was a second chart data path that had never run, waiting for someone to wire it up. Invented
 * candles in a screen whose whole purpose is reviewing what actually happened is the worst place in the product for
 * them, and "one adapter is the only chart data path" cannot be true while a second one is sitting in the tree
 * compiling. `tools/chart-lint` now fails the build if another appears (SM-P3-11, DEC-186).
 */

export function buildReview(c: Ctx) {
  const S = c.S;
  const REV_DAYS = c.data.core.revDays,
    BT_BOOKS = c.data.review.btBooks,
    RP_SESSION = c.data.review.replaySession,
    GRADING = c.data.review.replayGrading;
  const D = dayAt(REV_DAYS, S.jDay);
  const ents = D.entries;
  const wins = ents.filter((e) => e[3] > 0).length;

  // rules, discipline and the hourly flow all come off the entry list
  const offPlan = ents.filter((e) => e[4] === 'Asia Range Fade');
  const offPlan0 = offPlan[0];
  const RULES: [string, number][] =
    ents.length === 0
      ? [['No trades taken — nothing to breach', 1]]
      : [
          ['Stayed within max trades (' + ents.length + ' / 5)', ents.length <= 5 ? 1 : 0],
          ['No revenge trades logged', 1],
          ['All entries risk ≤ 0.5%', 1],
          offPlan0
            ? ['Took ' + offPlan0[0] + ' ' + offPlan0[4] + ' off-plan (no London confirm)', 0]
            : ['Every entry tagged to an armed strategy', 1],
          ['Journaled inside 24h', 1],
        ];
  const clean = RULES.filter((r) => r[1]).length;
  const cleanRatio = clean / RULES.length;
  const winRatio = ents.length ? wins / ents.length : 0;
  const disc = ents.length ? Math.round(100 * (0.6 * cleanRatio + 0.4 * winRatio)) : 0;

  // replay — scoped to a selected day
  const RD = dayAt(REV_DAYS, S.rpDay);
  const RT = RD.entries.map(toReplayTrade(RD));
  const hasRT = RT.length > 0;
  const rt: ReplayTrade = hasRT
    ? must(RT[Math.min(S.rpTrade, RT.length - 1)], 'replay trade')
    : {
        d: RD.short,
        sym: '—',
        side: '—',
        r: 0,
        pnl: 0,
        strat: '—',
        time: '—',
        grade: '—',
        note: 'No trades were taken on this day.',
      };
  const rpDayNet = RT.reduce((a, t) => a + t.pnl, 0);

  // replaySeries(): the seeded candle series the hand-drawn replay chart rendered. The widget charts the real
  // series and pins the trades to it, so there is nothing left to synthesise.
  const TOTAL = RP_SESSION?.totalBars ?? 0;
  const bar = Math.max(1, Math.min(TOTAL, S.rpBar));
  const jump = (n: number) => c.set({ rpBar: Math.max(1, Math.min(TOTAL, bar + n)) });
  const play = () => {
    c.timers.clear('replay');
    if (S.replayPlaying) {
      c.set({ replayPlaying: false });
      return;
    }
    c.set({ replayPlaying: true });
    c.timers.every(
      90,
      () => {
        // the reference reads self.state live inside the interval — the functional patch is that live read
        let ended = false;
        c.set((s) => {
          const b = s.rpBar + 2 * s.rpSpeed;
          if (b >= TOTAL) {
            ended = true;
            return { rpBar: TOTAL, replayPlaying: false };
          }
          return { rpBar: b };
        });
        if (ended) {
          c.timers.clear('replay');
          c.flash('Replay reached the end of the session', 'mut');
        }
      },
      'replay',
    );
  };

  return {
    // journal / eod shared day
    jShort: D.short,
    jMood: D.mood,
    jEnergy: 'Energy ' + D.energy + '/10',
    jModalOpen: S.jModal,
    jCloseModal: () => c.set({ jModal: false }),
    jfSym: S.jfSym,
    setJfSym: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      c.set({ jfSym: e.target.value, jfErr: '' }),
    jfR: S.jfR,
    setJfR: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      c.set({ jfR: e.target.value, jfErr: '' }),
    jfNote: S.jfNote,
    setJfNote: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      c.set({ jfNote: e.target.value, jfErr: '' }),
    jfSideBtns: ['LONG', 'SHORT'].map((v) => ({
      label: v,
      go: () => c.set({ jfSide: v }),
      bg: S.jfSide === v ? 'var(--rail)' : 'transparent',
      fg: S.jfSide === v ? 'var(--rail-ink)' : 'var(--muted)',
    })),
    jfStratBtns: ['London Open Reversal', 'Trend Continuation', 'Breakout Pullback', 'Asia Range Fade'].map(
      (v) => ({
        label: v,
        go: () => c.set({ jfStrat: v }),
        bd: S.jfStrat === v ? 'color-mix(in srgb, var(--sky-fill) 50%, transparent)' : 'var(--l10)',
        bg: S.jfStrat === v ? 'var(--l6)' : 'transparent',
        fg: S.jfStrat === v ? 'var(--text)' : 'var(--muted)',
      }),
    ),
    jfErr: S.jfErr,
    jfHasErr: !!S.jfErr,
    jfSubmit: () => {
      const sym = (S.jfSym || '').trim().toUpperCase();
      const r = parseFloat(S.jfR);
      if (!/^[A-Z0-9]{3,8}$/.test(sym)) return c.set({ jfErr: 'Enter a symbol of 3–8 letters or digits.' });
      if (isNaN(r)) return c.set({ jfErr: 'Enter the result in R, e.g. 1.4 or −0.8.' });
      if (!(S.jfNote || '').trim()) return c.set({ jfErr: 'A note is what makes the entry worth keeping.' });
      // the reference pushes straight into REV_DAYS — the demo day row is the ledger; trades() picks the entry up
      must(REV_DAYS[S.jDay], 'REV_DAYS[' + S.jDay + ']').entries.push([
        sym,
        S.jfSide,
        r,
        Math.round(r * 800),
        S.jfStrat,
        '12:00',
        r >= 1 ? 'B' : 'C',
        S.jfNote.trim(),
        ['Manual entry'],
      ]);
      c.set({ jModal: false, jfSym: '', jfR: '', jfNote: '' });
      c.flash(sym + ' entry added to ' + D.short, 'pos');
    },

    // eod
    eodDisc: disc ? String(disc) : '—',
    eodDiscFg:
      disc >= 80 ? 'var(--pos)' : disc >= 60 ? 'var(--amb-2)' : disc ? 'var(--neg)' : 'var(--muted2)',

    // replay
    rpDays: REV_DAYS.map((d, i) => {
      const n = d.entries.length;
      return {
        label: d.short,
        sub: n === 0 ? 'No trades' : n === 1 ? '1 trade' : n + ' trades',
        go: () => {
          c.timers.clear('replay');
          c.set({ rpDay: i, rpTrade: 0, rpBar: 1284, replayPlaying: false });
        },
        bg: i === S.rpDay ? 'var(--rail)' : 'transparent',
        fg: i === S.rpDay ? 'var(--rail-ink)' : 'var(--muted)',
        subFg: i === S.rpDay ? 'var(--rail-ink)' : 'var(--muted2)',
        fw: i === S.rpDay ? '600' : '500',
      };
    }),
    rpDayLabel: RD.label,
    rpDayNet: rpDayNet === 0 ? '$0.00' : c.fmt(rpDayNet),
    rpDayNetFg: rpDayNet >= 0 ? 'var(--pos)' : 'var(--neg)',
    rpHasTrades: hasRT,
    rpNoTrades: !hasRT,
    rpTradesOpen: S.rpTradesOpen && hasRT,
    rpTradesToggle: () => c.set((s) => ({ rpTradesOpen: !s.rpTradesOpen })),
    rpTradesIcon: S.rpTradesOpen ? 'ph-caret-down' : 'ph-caret-up',
    rpTradesToggleLabel: S.rpTradesOpen ? 'Collapse trades list' : 'Expand trades list',
    rpTradesSummary: RT.length === 0 ? 'None' : RT.length === 1 ? '1 trade' : RT.length + ' trades',
    rpLoadedLabel: hasRT ? rt.sym + ' ' + rt.side + ' · ' + rt.time : '—',
    // Only the charted symbol's own trades can be pinned to its series — a EURUSD entry on an XAUUSD chart would be
    // an arrow at a price that never traded.
    rpMarkers: hasRT
      ? tvMarkers(
          rt.sym,
          '60',
          RT.filter((t) => t.sym === rt.sym).map((t) => ({
            time: t.time,
            side: t.side,
            text: `${t.sym} ${t.r >= 0 ? '+' : '−'}${Math.abs(t.r).toFixed(2)}R`,
          })),
        )
      : '[]',
    // Said out loud when trades are missing from the chart rather than letting the count silently disagree with the
    // list beside it.
    rpMarkerNote:
      hasRT && RT.length > RT.filter((t) => t.sym === rt.sym).length
        ? `${RT.filter((t) => t.sym === rt.sym).length} of ${RT.length} trades marked · other symbols not on this series`
        : '',
    rpOutcome: hasRT ? (rt.pnl >= 0 ? 'Closed at take profit' : 'Closed at stop loss') : '',
    rpOutcomeFg: hasRT && rt.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
    rpTrades: RT.map((t, i) => {
      const sel = i === S.rpTrade;
      return {
        sym: t.sym,
        side: t.side,
        strat: t.strat,
        time: t.time,
        grade: t.grade,
        r: (t.r >= 0 ? '+' : '−') + Math.abs(t.r).toFixed(2) + 'R',
        pnl: c.fmt(t.pnl),
        pnlFg: t.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
        sideFg: t.side === 'LONG' ? 'var(--pos)' : 'var(--neg)',
        sideBg:
          t.side === 'LONG'
            ? 'color-mix(in srgb, var(--pos) 12%, transparent)'
            : 'color-mix(in srgb, var(--neg-fill) 14%, transparent)',
        sel,
        rowBg: sel ? 'var(--l6)' : 'transparent',
        nameFg: sel ? 'var(--text)' : 'var(--text2)',
        markBd: sel ? 'color-mix(in srgb, var(--sky-fill) 55%, transparent)' : 'var(--l10)',
        markBg: sel ? 'color-mix(in srgb, var(--sky-fill) 14%, transparent)' : 'transparent',
        markFg: sel ? 'var(--sky)' : 'var(--faint)',
        markIcon: sel ? 'ph-play-circle' : 'ph-play',
        markLabel: sel ? 'Loaded' : 'Replay',
        go: () => {
          c.timers.clear('replay');
          c.set({ rpTrade: i, rpBar: 1284, replayPlaying: false });
        },
      };
    }),
    rpSym: rt.sym,
    rpSide: rt.side,
    rpStrat: rt.strat,
    rpSideFg: rt.side === 'LONG' ? 'var(--pos)' : 'var(--neg)',
    rpMeta: (
      [
        ['Symbol', rt.sym],
        ['Dir', rt.side, rt.side === 'LONG' ? 'var(--pos)' : 'var(--neg)'],
        [
          'Result',
          (rt.r >= 0 ? '+' : '−') + Math.abs(rt.r).toFixed(2) + 'R',
          rt.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
        ],
        ['P&L', c.fmt(rt.pnl), rt.pnl >= 0 ? 'var(--pos)' : 'var(--neg)'],
        ['Grade', rt.grade],
        ['Opened', rt.time],
      ] as [string, string, string?][]
    ).map(([k, v, col]) => ({ k, v, c: col || 'var(--text2)' })),
    rpSess: (
      [
        ['Series', S.tvReady ? 'TradingView H1' : 'Reconstructed H1'],
        ['Session', rt.d],
        ['Strategy', rt.strat],
        ['Account', RP_SESSION?.account ?? '—'],
        ['Risk/Trade', RP_SESSION?.riskPerTrade ?? '—'],
        ['Bar', bar.toLocaleString('en-US') + ' / ' + TOTAL.toLocaleString('en-US')],
      ] as [string, string][]
    ).map(([k, v]) => ({ k, v })),
    rpBarLabel: 'Bar ' + bar.toLocaleString('en-US') + ' / ' + TOTAL.toLocaleString('en-US'),
    // scaleX on the compositor rather than an animated width. 90ms linear, because this one tracks playback rather
    // than making an entrance.
    rpProgScale: (bar / TOTAL).toFixed(4),
    rpPlaying: S.replayPlaying,
    rpIcon: S.replayPlaying ? 'ph-pause' : 'ph-play',
    rpPlayLabel: S.replayPlaying ? 'Pause replay' : 'Play replay',
    rpPlay: play,
    rpBack: () => {
      c.timers.clear('replay');
      c.set({ replayPlaying: false });
      jump(-50);
    },
    rpFwd: () => {
      c.timers.clear('replay');
      c.set({ replayPlaying: false });
      jump(50);
    },
    rpSpeedLabel: S.rpSpeed + '×',
    rpCycleSpeed: () =>
      c.set((s) => ({ rpSpeed: s.rpSpeed >= 4 ? 0.5 : s.rpSpeed === 0.5 ? 1 : s.rpSpeed * 2 })),
    rpNote: rt.note,
    // The grading rubric arrives with the replay bundle, so no bundle means no grades — an empty card, which
    // is what a trader who has not prepared a replay should see. Reading through a null rubric to render one was
    // how a screen with nothing on it became a product with nothing on it.
    rpAi: !GRADING
      ? []
      : [
          {
            label: GRADING.entry.label,
            score:
              rt.r >= 2
                ? GRADING.entry.precise.score
                : rt.r >= 0
                  ? GRADING.entry.acceptable.score
                  : GRADING.entry.poor.score,
            tag:
              rt.r >= 2
                ? GRADING.entry.precise.tag
                : rt.r >= 0
                  ? GRADING.entry.acceptable.tag
                  : GRADING.entry.poor.tag,
            col: rt.r >= 0 ? 'var(--sky)' : 'var(--neg)',
            narr: rt.r >= 0 ? GRADING.entry.narrGood : GRADING.entry.narrBad,
          },
          {
            label: GRADING.exit.label,
            score: rt.pnl >= 0 ? GRADING.exit.held.score : GRADING.exit.stopped.score,
            tag: rt.pnl >= 0 ? GRADING.exit.held.tag : GRADING.exit.stopped.tag,
            col: rt.pnl >= 0 ? 'var(--pos)' : 'var(--amb-2)',
            narr: rt.pnl >= 0 ? GRADING.exit.narrGood : GRADING.exit.narrBad,
          },
          {
            label: GRADING.plan.label,
            score:
              rt.note.indexOf('Off-plan') >= 0 || rt.strat === 'Asia Range Fade'
                ? GRADING.plan.off.score
                : GRADING.plan.on.score,
            tag: rt.strat === 'Asia Range Fade' ? GRADING.plan.off.tag : GRADING.plan.on.tag,
            col: rt.strat === 'Asia Range Fade' ? 'var(--neg)' : 'var(--pos)',
            narr: rt.strat === 'Asia Range Fade' ? GRADING.plan.narrBad : GRADING.plan.narrGood,
          },
        ],

    // backtest
    rpModeBtns: (
      [
        ['replay', 'Replay', 'ph-film-slate'],
        ['backtest', 'Backtest', 'ph-flask'],
      ] as [string, string, string][]
    ).map(([id, label, icon]) => ({
      label,
      icon,
      go: () => c.set({ rpMode: id }),
      bg: S.rpMode === id ? 'var(--rail)' : 'transparent',
      fg: S.rpMode === id ? 'var(--rail-ink)' : 'var(--muted)',
      fw: S.rpMode === id ? '600' : '500',
    })),
    rpIsReplay: S.rpMode === 'replay',
    rpIsBacktest: S.rpMode === 'backtest',
    btStratBtns: Object.keys(BT_BOOKS).map((k) => ({
      label: k,
      go: () => c.set({ btStrat: k, btWindow: must(BT_BOOKS[k], 'BT_BOOKS[' + k + ']').window }),
      bd: S.btStrat === k ? 'color-mix(in srgb, var(--sky-fill) 50%, transparent)' : 'var(--l10)',
      bg: S.btStrat === k ? 'var(--l6)' : 'transparent',
      fg: S.btStrat === k ? 'var(--text)' : 'var(--muted)',
    })),
    btWindow: S.btWindow,
    btRisk: S.btRisk,
    btRiskLabel: S.btRisk.toFixed(2) + '%',
    setBtRisk: (e: ChangeEvent<HTMLInputElement>) => c.set({ btRisk: +e.target.value }),
    btMonths: S.btMonths,
    btMonthsLabel: S.btMonths + (S.btMonths === 1 ? ' month' : ' months'),
    setBtMonths: (e: ChangeEvent<HTMLInputElement>) => c.set({ btMonths: +e.target.value }),
    btCap: S.btCap,
    btCapLabel: S.btCap + (S.btCap === 1 ? ' trade / session' : ' trades / session'),
    setBtCap: (e: ChangeEvent<HTMLInputElement>) => c.set({ btCap: +e.target.value }),
    btBusy: S.btBusy,
    btRunLabel: S.btBusy ? 'Running…' : S.btRun ? 'Re-run backtest' : 'Run backtest',
    btGo: () => {
      if (S.btBusy) return;
      c.set({ btBusy: true });
      c.timers.after(480, () => runBacktest(c));
    },
    btHasRun: !!S.btRun && !S.btBusy,
    btNoRun: !S.btRun && !S.btBusy,
    btResultsOpen: S.btResultsOpen,
    btResultsToggle: () => c.set((s) => ({ btResultsOpen: !s.btResultsOpen })),
    btResultsIcon: S.btResultsOpen ? 'ph-caret-down' : 'ph-caret-up',
    btResultsLabel: S.btResultsOpen ? 'Collapse results' : 'Expand results',
    ...(S.btRun ? btResults(S.btRun) : {}),

    // account progress
    isAcctProgress: S.screen === 'acctprogress',
  } satisfies Partial<ViewModel>;
}
export type ReviewModel = ReturnType<typeof buildReview>;

/** The backtest result block the reference spreads in when `btRun` is set (equity curve path, stats grid, assumptions, verdict). */
function btResults(R: BacktestRun) {
  return {
    // btPath/btFill/btZeroY/btCurveFg: the hand-drawn equity curve went the way of the hand-drawn candles — the
    // backtest charts through the TradingView widget now. Dead keys, omitted.
    btHeadline: (R.netR >= 0 ? '+' : '−') + Math.abs(R.netR).toFixed(1) + 'R',
    btHeadlineFg: R.netR >= 0 ? 'var(--pos)' : 'var(--neg)',
    btSub: R.n + ' trades over ' + R.days + ' sessions · ' + R.strat,
    btStats: [
      { k: 'Win rate', v: (R.wr * 100).toFixed(1) + '%', c: R.wr >= 0.5 ? 'var(--pos)' : 'var(--amb-2)' },
      {
        k: 'Expectancy',
        v: (R.expectancy >= 0 ? '+' : '−') + Math.abs(R.expectancy).toFixed(2) + 'R',
        c: R.expectancy >= 0 ? 'var(--pos)' : 'var(--neg)',
      },
      { k: 'Profit factor', v: R.pf.toFixed(2), c: R.pf >= 1 ? 'var(--pos)' : 'var(--neg)' },
      { k: 'Avg win', v: '+' + R.avgWin.toFixed(2) + 'R', c: 'var(--pos)' },
      { k: 'Avg loss', v: '−' + Math.abs(R.avgLoss).toFixed(2) + 'R', c: 'var(--neg)' },
      { k: 'Max drawdown', v: '−' + R.maxDD.toFixed(1) + 'R', c: 'var(--neg)' },
      {
        k: 'Worst losing streak',
        v: R.worstStreak + (R.worstStreak === 1 ? ' trade' : ' trades'),
        c: 'var(--text2)',
      },
      { k: 'Wins / losses', v: R.wins + ' / ' + R.losses, c: 'var(--text2)' },
    ].map((s, i, arr) =>
      Object.assign(s, {
        br: (i + 1) % 4 === 0 ? 'none' : '1px solid var(--l6)',
        bb: i >= arr.length - (arr.length % 4 || 4) ? 'none' : '1px solid var(--l6)',
      }),
    ),
    btAssumptions: [
      { k: 'Risk per trade', v: R.risk.toFixed(2) + '% of equity' },
      { k: 'Session window', v: R.window },
      { k: 'Entry cap', v: R.cap + ' per session' },
      { k: 'Period', v: R.months + ' months · ' + R.days + ' sessions' },
    ],
    btVerdict:
      R.expectancy >= 0.3
        ? 'Holds its edge under these assumptions — the cap is doing the work.'
        : R.expectancy >= 0
          ? 'Marginally positive; the edge is thin enough that costs and slippage matter.'
          : 'Negative expectancy under these assumptions — do not arm this book.',
    btVerdictFg: R.expectancy >= 0.3 ? 'var(--pos)' : R.expectancy >= 0 ? 'var(--amb-2)' : 'var(--neg)',
  };
}
